import type { ResidentNeed } from '@lmc/ts-common'
import { getJSON, postJSON } from 'common/services/api'
import { showToast } from 'common/utils'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import type { ProfileNeedOption } from 'screens/Residents/screens/ResidentNeedsScreen/ResidentNeedsScreen'
import { getResidentBaseFunction } from 'selectors/NeedsAndSkills'
import type { BaseResidentNeed } from 'types/shared'

export const RESIDENT_NEEDS_QUERY_KEY = 'residentNeeds'

const LEGACY_CATEGORIES_URL = 'list/skill-need-templates/categories?needs=1'
const getLegacyNeedsUrl = (id: string) => `/api/list/residents/${id}/needs`

export type ResidentNeedV6 = BaseResidentNeed & {
  carePlans?: Array<{ id: string; name: string }>
}

type ResidentNeedsResponse = {
  data: ResidentNeedV6[]
  pagination: {
    totalItems: number
    currentPage: number
    itemsPerPage: number
  }
}

const normalizeLegacyNeeds = (
  id: string,
  categoriesResult: ProfileNeedOption[] | undefined,
  needsResult: ResidentNeed[] | undefined
): ResidentNeedV6[] => {
  const raw = getResidentBaseFunction(id, categoriesResult, needsResult) as
    | Array<ResidentNeed & { needName: string }>
    | undefined

  return (raw ?? []).map((n) => ({
    id: String(n.id),
    need: { id: n.need as string, name: n.needName },
    notes: n.notes,
    addedOn: n.addedOn ? new Date(n.addedOn).toISOString() : undefined,
    carePlans: undefined
  }))
}

const useResidentNeeds = ({ id }: { id: string }) => {
  const { data: flags, isLoading: isFlagsLoading } = useFeatureFlags({
    flagNames: ['mods_needs_resident_profile']
  })
  const isV6Enabled = flags.mods_needs_resident_profile?.enabled ?? false

  const { isLoading: isLoadingCategories, data: categoriesData } = useQuery(
    ['skill-need-templates-categories-need'],
    () => getJSON({ url: LEGACY_CATEGORIES_URL }),
    { enabled: !!id && !isFlagsLoading && !isV6Enabled }
  )

  const { isLoading: isLoadingLegacyNeeds, data: legacyNeedsData } = useQuery(
    ['resident-needs', id],
    () => getJSON({ url: getLegacyNeedsUrl(id) }),
    { enabled: !!id && !isFlagsLoading && !isV6Enabled }
  )

  const {
    data: v6Data,
    isLoading: isLoadingV6,
    refetch
  } = useQuery<ResidentNeedsResponse>(
    [RESIDENT_NEEDS_QUERY_KEY, id],
    () => getJSON({ url: `/v6/residents/${id}/needs` }),
    { enabled: !!id && !isFlagsLoading && isV6Enabled }
  )

  if (isV6Enabled) {
    return {
      needs: v6Data?.data ?? [],
      pagination: v6Data?.pagination,
      isLoading: isFlagsLoading || isLoadingV6,
      refetch
    }
  }

  return {
    needs: normalizeLegacyNeeds(
      id,
      categoriesData?.result,
      legacyNeedsData?.result
    ),
    pagination: undefined,
    isLoading: isFlagsLoading || isLoadingCategories || isLoadingLegacyNeeds,
    refetch: async () => {}
  }
}

export const useResidentNeedAssign = (
  residentId: string,
  onSuccess?: () => void
) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const mutation = useMutation(
    async (payload: { needId: string; notes?: string }) => {
      return postJSON({
        url: `/v6/residents/${residentId}/needs`,
        json: payload
      })
    },
    {
      onSuccess: () => {
        showToast({ title: t('skillsAndNeeds.needAdded') })
        queryClient.invalidateQueries([RESIDENT_NEEDS_QUERY_KEY, residentId])
        onSuccess?.()
      },
      onError: (error: any) => {
        showToast({
          type: 'error',
          title: error?.message || t('skillsAndNeeds.needAddFailed')
        })
      }
    }
  )

  return {
    assignNeed: mutation.mutateAsync,
    isAssigning: mutation.isLoading
  }
}

export const useResidentNeedUpdate = (
  residentId: string,
  onSuccess?: () => void
) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const mutation = useMutation(
    async ({
      assignmentId,
      notes
    }: {
      assignmentId: string
      notes?: string
    }) => {
      return postJSON({
        url: `/v6/residents/${residentId}/needs/${assignmentId}`,
        method: 'PUT',
        json: { notes }
      })
    },
    {
      onSuccess: () => {
        showToast({ title: t('skillsAndNeeds.changesSaved') })
        queryClient.invalidateQueries([RESIDENT_NEEDS_QUERY_KEY, residentId])
        queryClient.invalidateQueries(['care-plans'])
        onSuccess?.()
      },
      onError: (error: any) => {
        showToast({
          type: 'error',
          title: error?.message || t('skillsAndNeeds.needUpdateFailed')
        })
      }
    }
  )

  return {
    updateNeed: mutation.mutateAsync,
    isUpdating: mutation.isLoading
  }
}

export const useResidentNeedUnassign = (
  residentId: string,
  onSuccess?: () => void
) => {
  const { t } = useTranslation()
  const queryClient = useQueryClient()

  const mutation = useMutation(
    async (assignmentId: string) => {
      return postJSON({
        url: `/v6/residents/${residentId}/needs/${assignmentId}`,
        method: 'DELETE',
        responseType: 'text'
      })
    },
    {
      onSuccess: () => {
        showToast({ title: t('skillsAndNeeds.needDeleted') })
        queryClient.invalidateQueries([RESIDENT_NEEDS_QUERY_KEY, residentId])
        queryClient.invalidateQueries(['care-plans'])
        onSuccess?.()
      },
      onError: (error: any) => {
        showToast({
          type: 'error',
          title: error?.message || t('skillsAndNeeds.needDeleteFailed')
        })
      }
    }
  )

  return {
    unassignNeed: mutation.mutateAsync,
    isUnassigning: mutation.isLoading
  }
}

export default useResidentNeeds
export type { ResidentNeedsResponse }
